// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getCookie, setCookie } from "@insite/mobius/utilities/cookies";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MenuClose from "@steiner/XDL/Icons/MenuClose";
import * as React from "react";

const enum fields {
    message = "message",
    bgColor = "bgColor",
    textColor = "textColor",
    dismissible = "dismissible",
    hideAfterClose = "hideAfterClose",
}

interface Props extends WidgetProps {
    fields: {
        [fields.message]: string;
        [fields.bgColor]: string;
        [fields.textColor]: string;
        [fields.dismissible]: boolean;
        [fields.hideAfterClose]: boolean;
    };
}

export interface GlobalNotificationBarStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    message?: TypographyProps;
    closeIcon?: IconProps;
}

export const globalNotificationBarStyles: GlobalNotificationBarStyles = {};

const styles = globalNotificationBarStyles;

const GlobalNotificationBar: React.FunctionComponent<Props> = (props: Props) => {
    const [visible, setVisibility] = React.useState(false);
    const {
        id,
        fields: { message, bgColor, textColor, dismissible, hideAfterClose },
    } = props;

    React.useEffect(() => {
        if (!hideAfterClose || (hideAfterClose && !getCookie(`GlobalMessage-${id}`))) {
            setVisibility(true);
        }
    }, []);

    if (!visible || !message) {
        return null;
    }

    const handleMessageClose = () => {
        setVisibility(false);
        setCookie(`GlobalMessage-${id}`, "true");
    };

    return (
        <StyledWrapper {...styles.wrapper} {...{ bgColor }}>
            <StyledWrapper {...styles.container}>
                <Typography {...styles.message} color={textColor}>
                    {message}
                </Typography>
                {dismissible && <Icon src={MenuClose} {...styles.closeIcon} onClick={handleMessageClose} />}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: GlobalNotificationBar,
    definition: {
        group: "Basic",
        icon: "Info",
        fieldDefinitions: [
            {
                name: fields.message,
                displayName: "Message",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#d03f33",
                fieldType: "General",
                isRequired: true,
                sortOrder: 2,
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#ffffff",
                fieldType: "General",
                isRequired: true,
                sortOrder: 3,
            },
            {
                name: fields.dismissible,
                displayName: "Dismissible",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.hideAfterClose,
                displayName: "Hide After Close",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
                tooltip: "Once the user close the message, it will not appear again during future visits to the site.",
                isVisible: item => item.fields.dismissible,
            },
        ],
        allowedContexts: ["Header", "CompactHeader"],
    },
};

export default widgetModule;
